import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Power, AlertTriangle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useLocalization } from '@/hooks/useLocalization';

interface KillSwitchRow {
  id: string;
  is_active: boolean;
  reason: string | null;
  activated_by: string | null;
  activated_at: string | null;
  deactivated_at: string | null;
  updated_at: string;
}

const KillSwitchPanel = () => {
  const { t } = useLocalization();
  const { user } = useAuth();
  const { role } = useUserRole();
  const [state, setState] = useState<KillSwitchRow | null>(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const canToggle = role === 'pmo' || role === 'admin';

  const load = async () => {
    const { data } = await supabase
      .from('kill_switch_state')
      .select('*')
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (data) setState(data as KillSwitchRow);
  };

  useEffect(() => {
    load();
    const channel = supabase
      .channel('kill-switch')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'kill_switch_state' }, load)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const toggle = async (activate: boolean) => {
    if (!state || !user) return;
    if (activate && !reason.trim()) {
      toast.error(t('Reason required to activate kill switch'));
      return;
    }
    setLoading(true);
    const update = activate
      ? { is_active: true, reason: reason.trim(), activated_by: user.id, activated_at: new Date().toISOString() }
      : { is_active: false, deactivated_by: user.id, deactivated_at: new Date().toISOString() };

    const { error } = await supabase.from('kill_switch_state').update(update).eq('id', state.id);
    if (error) {
      toast.error(error.message);
    } else {
      await supabase.from('kill_switch_events').insert({
        actor_id: user.id,
        event_type: activate ? 'activated' : 'deactivated',
        reason: activate ? reason.trim() : 'Resumed normal operation',
      });
      toast.success(activate ? t('AI outputs halted') : t('AI outputs resumed'));
      setReason('');
    }
    setLoading(false);
  };

  const isActive = state?.is_active ?? false;

  return (
    <Card className={isActive ? 'border-destructive' : ''}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Power className={isActive ? 'text-destructive' : 'text-muted-foreground'} />
              
              {t('AI Kill Switch')}
            </CardTitle>
            <CardDescription>
              
              {t('Immediate halt of all AI-generated outputs across the platform')}
            </CardDescription>
          </div>
          <Badge variant={isActive ? 'destructive' : 'secondary'}>
            {isActive ? 'HALTED' : 'OPERATIONAL'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isActive && state?.reason && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 text-sm">
            <AlertTriangle className="w-4 h-4 mt-0.5 text-destructive flex-shrink-0" />
            <div>
              <p className="font-medium">{t('Reason:')}</p>
              <p className="text-muted-foreground">{state.reason}</p>
              {state.activated_at && (
                <p className="text-xs text-muted-foreground mt-1">
                  
                  {t('Activated')} {new Date(state.activated_at).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        )}

        {!isActive && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-muted/40 text-sm">
            <ShieldCheck className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
            <p className="text-muted-foreground">
              
              {t('All AI modules are operating normally. Outputs flow through HITL review.')}
            </p>
          </div>
        )}

        {canToggle ? (
          <>
            {!isActive && (
              <Textarea
                placeholder={t('Reason for halting AI outputs (required)')}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
              />
            )}
            <Button
              variant={isActive ? 'default' : 'destructive'}
              onClick={() => toggle(!isActive)}
              disabled={loading}
              className="w-full"
            >
              <Power className="w-4 h-4 me-2" />
              {isActive ? t('Resume AI Outputs') : t('Activate Kill Switch')}
            </Button>
          </>
        ) : (
          <p className="text-xs text-muted-foreground italic">
            
            {t('Only PMO Head or Admin can toggle the kill switch.')}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default KillSwitchPanel;
